/** `rule.claimTiles` — claim the floor by walking it. Every surface carrying
 * `surfaceTag` is split into equal cells about `cellW` wide; a claimer
 * standing on it claims the cell under its centre (scored to its seat). A
 * claimed cell stays claimed. Claimed spans are drawn by `marker` entities
 * (static, one per contiguous span, kept in step with the cells), and a
 * fully claimed surface sheds `doneTag` — so `flow.director` with that as its
 * required tag clears the phase when the last surface is done. Surfaces that
 * leave the world (a replaced phase layout) take their markers with them. */
import type { JsonObject } from '../../json.js';
import { id, int, num, obj } from '../schemaBuilders.js';
import { defineModule, type ModuleCtx } from '../module.js';
import { deltaX, removeTag, type EntityV1 } from '../world.js';

export type ClaimConfig = {
  claimerTag: string;
  surfaceTag: string;
  cellW: number;
  score: number;
  /** Static archetype drawn over each claimed span. */
  marker: string;
  /** Tag removed from a surface once all its cells are claimed. */
  doneTag: string;
};

type Ctx = ModuleCtx<ClaimConfig>;

/** Solver pull of an unclaimed cell. */
const CLAIM_WEIGHT = 3;

function cellCount(c: ClaimConfig, s: EntityV1): number {
  return Math.max(1, Math.round(s.w / c.cellW));
}

/** The surface's claim bitmap ('0'/'1' per cell), created on first sight. */
function cellsOf(ctx: Ctx, s: EntityV1): string {
  const cells = ctx.state.cells as JsonObject;
  const known = cells[s.id];
  return typeof known === 'string' ? known : '0'.repeat(cellCount(ctx.config, s));
}

/** Contiguous claimed spans as [first, last] cell indices. */
function spans(cells: string): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] !== '1') continue;
    if (out.length && out[out.length - 1]![1] === i - 1) out[out.length - 1]![1] = i;
    else out.push([i, i]);
  }
  return out;
}

function syncMarkers(ctx: Ctx, s: EntityV1, cells: string): void {
  const markers = ctx.state.markers as JsonObject;
  const ids = ((markers[s.id] ?? []) as string[]).filter(m => ctx.entity(m));
  const w = s.w / cells.length, left = s.x - s.w / 2;
  const wanted = spans(cells);
  wanted.forEach(([a, b], i) => {
    const x = left + ((a + b + 1) / 2) * w, width = (b - a + 1) * w;
    const existing = ctx.entity(ids[i] ?? null);
    if (existing) { existing.x = x; existing.w = width; existing.h = s.h; existing.y = s.y; }
    else ids.push(ctx.spawn(ctx.config.marker, { x, y: s.y, params: { w: width, h: s.h } }).id);
  });
  for (const extra of ids.splice(wanted.length)) ctx.despawn(ctx.entity(extra)!, 'merged');
  markers[s.id] = ids;
}

export const claimTiles = defineModule<ClaimConfig>({
  id: 'rule.claimTiles', version: 1, attach: 'global', role: 'rule',
  summary: 'Walk-to-claim floors: tagged surfaces split into cells; a standing claimer claims the cell under it (scored); claimed spans are drawn; a fully claimed surface sheds its required tag.',
  config: obj({ claimerTag: id(), surfaceTag: id(), cellW: num(0.1, 8), score: int(0, 100000), marker: id(), doneTag: id() }),
  requires: ['body.surface'], events: ['claim', 'claim-complete'],
  presentation: { claim: { cue: 'collect' }, 'claim-complete': { cue: 'success', vfx: { effect: 'flash', palette: 'slot' } } },
  proofs: ['standing-claims-cell-under-centre', 'claimed-stays-claimed', 'airborne-claims-nothing', 'spans-merge-into-one-marker',
    'complete-sheds-done-tag', 'markers-follow-replaced-surfaces', 'claimed-percent-hud'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, marker: rename(c.marker) }),
  check: (c, context) => (context.archetypes[c.marker]?.modules.includes('body.static') ? [] : [`marker ${c.marker} must use body.static`]),
  hud: () => [{ id: 'claimed', label: 'CLAIMED', kind: 'counter' }],
  hudValues(ctx) {
    let total = 0, claimed = 0;
    for (const s of ctx.entities({ tag: ctx.config.surfaceTag, status: 'any' })) {
      const cells = cellsOf(ctx, s);
      total += cells.length;
      claimed += cells.split('').filter(ch => ch === '1').length;
    }
    return { claimed: total ? Math.floor((claimed * 100) / total) : 0 };
  },
  initGlobal(ctx) { Object.assign(ctx.state, { cells: {}, markers: {} }); },
  systems: {
    interact(ctx) {
      const c = ctx.config;
      const cells = ctx.state.cells as JsonObject, markers = ctx.state.markers as JsonObject;
      for (const sid of Object.keys(cells)) {
        if (ctx.entity(sid)) continue;
        for (const m of (markers[sid] ?? []) as string[]) { const e = ctx.entity(m); if (e) ctx.despawn(e, 'surface-gone'); }
        delete cells[sid]; delete markers[sid];
      }
      for (const claimer of ctx.entities({ tag: c.claimerTag, status: 'active' })) {
        if (!claimer.grounded) continue;
        const s = ctx.entity(claimer.groundId);
        if (!s || !s.tags.includes(c.surfaceTag)) continue;
        const bitmap = cellsOf(ctx, s);
        const w = s.w / bitmap.length;
        const index = Math.min(bitmap.length - 1, Math.max(0, Math.floor((deltaX(ctx.world, s.x, claimer.x) + s.w / 2) / w)));
        if (bitmap[index] === '1') continue;
        const next = bitmap.slice(0, index) + '1' + bitmap.slice(index + 1);
        cells[s.id] = next;
        if (claimer.slot !== null) ctx.addScore(claimer.slot, c.score);
        ctx.emit('claim', { x: s.x - s.w / 2 + (index + 0.5) * w, y: s.y - s.h, slot: claimer.slot ?? 0, points: c.score });
        syncMarkers(ctx, s, next);
        if (!next.includes('0')) {
          removeTag(s, c.doneTag);
          ctx.emit('claim-complete', { x: s.x, y: s.y - s.h, slot: claimer.slot ?? 0 });
        }
      }
    },
  },
  solverProgress(ctx) {
    let claimed = 0;
    for (const s of ctx.entities({ tag: ctx.config.surfaceTag, status: 'any' })) claimed += cellsOf(ctx, s).split('1').length - 1;
    return claimed * (CLAIM_WEIGHT + 1);
  },
  solverTargets(ctx) {
    const out = [];
    for (const s of ctx.entities({ tag: ctx.config.surfaceTag })) {
      const bitmap = cellsOf(ctx, s), w = s.w / bitmap.length;
      for (let i = 0; i < bitmap.length; i++) {
        if (bitmap[i] === '0') out.push({ x: s.x - s.w / 2 + (i + 0.5) * w, y: s.y - s.h, weight: CLAIM_WEIGHT, label: 'claim' });
      }
    }
    return out;
  },
});
